import type { LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { PageHeader } from '@/components/PageHeader'
import { formatRelative } from '@/lib/format'
import { EmptyState, PageError, PageLoading } from '@/components/PageState'

interface BBPageProps {
  title: string
  description?: string
  icon?: LucideIcon
  actions?: ReactNode
  /** First-load state: renders the skeleton instead of children. */
  loading?: boolean
  /** Expectation-setter above the skeleton (e.g. "this query takes minutes"). */
  loadingMessage?: string
  /** Background refetch state: spins the refresh button only. */
  refreshing?: boolean
  error?: string | Error | null
  onRefresh?: () => void
  /** When the data was last fetched/computed — rendered on the refresh button */
  updatedAt?: Date | string | null
  /** Render the empty state instead of children */
  empty?: boolean
  emptyProps?: { title?: string; description?: string }
  children: ReactNode
}

/**
 * Standard page shell: header + exactly one of error / loading / empty /
 * content. A fetch error always renders loud — never zeroed-out metrics.
 */
export function BBPage({
  title,
  description,
  icon,
  actions,
  loading,
  loadingMessage,
  refreshing,
  error,
  onRefresh,
  updatedAt,
  empty,
  emptyProps,
  children,
}: BBPageProps) {
  return (
    <div className="space-y-4">
      <PageHeader
        title={title}
        description={description}
        icon={icon}
        actions={actions}
        loading={loading || refreshing}
        onRefresh={onRefresh}
        refreshLabel={updatedAt ? `Updated ${formatRelative(updatedAt)}` : undefined}
      />
      {error ? (
        <PageError error={error} onRetry={onRefresh} retrying={loading || refreshing} />
      ) : loading ? (
        <>
          {loadingMessage && (
            <p className="text-center text-sm text-muted-foreground">{loadingMessage}</p>
          )}
          <PageLoading />
        </>
      ) : empty ? (
        <EmptyState {...emptyProps} />
      ) : (
        children
      )}
    </div>
  )
}
